import React from 'react';
import { type paths } from '../../../endpoint/Schema';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { TabView, TabPanel } from 'primereact/tabview';
import useTenantStore, { selectTenantId } from '../../../stores/TenantStore';
import OpenAPI from '../../../endpoint/Openapi';
import AppleStoreTab from './AppleStoreTab';
import GooglePlayStoreTab from './GooglePlayStoreTab';

function StoreConfiguration() {
  const tenantId = useTenantStore(selectTenantId);
  const tenantData = useTenantStore();
  const [featureSettings, setFeatureSettings] = React.useState<
    | paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json']
    | undefined
  >({});
  const [loading, setLoading] = React.useState(false);
  const toast = React.useRef<Toast>(null);

  // Main tab state (Apple vs Google Play Store)
  const [activeMainTabIndex, setActiveMainTabIndex] = React.useState(0);

  // Internal tab states for each store (App Information vs Screenshots)
  const [appleActiveTabIndex, setAppleActiveTabIndex] = React.useState(0);
  const [googleActiveTabIndex, setGoogleActiveTabIndex] = React.useState(0);

  // Apple Store Config
  const appleConfig = featureSettings?.appleStoreConfig ?? {};
  const googleConfig = featureSettings?.googlePlayConfig ?? {};

  const fetchSettings = React.useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);

    try {
      const res = await OpenAPI.GET('/api/v2/admin/feature-setting', {
        params: {
          query: {
            tenantId,
          },
        },
      });

      if (res) {
        setFeatureSettings(res.data);
      }
    } catch {}

    setLoading(false);
  }, [tenantId]);

  React.useEffect(() => {
    void fetchSettings();
  }, [fetchSettings]);

  return (
    <div>
      {loading ? (
        <Skeleton width="100rem" height="100rem" />
      ) : (
        <div>
          <div className="flex flex-col justify-center w-full px-6 py-8 rounded-3xl shadow-[0px_4px_12px_rgba(0,0,0,0.1)] text-base leading-6 text-black bg-white">
            <div className="text-3xl font-medium mb-6">Store Configuration</div>

            <TabView
              activeIndex={activeMainTabIndex}
              onTabChange={(e) => {
                setActiveMainTabIndex(e.index);
              }}
            >
              <TabPanel header="🍎 Apple App Store">
                <AppleStoreTab
                  appleConfig={appleConfig}
                  featureSettings={featureSettings}
                  setFeatureSettings={setFeatureSettings}
                  loading={loading}
                  setLoading={setLoading}
                  tenantId={tenantId}
                  toast={toast}
                  fetchSettings={fetchSettings}
                  activeTabIndex={appleActiveTabIndex}
                  setActiveTabIndex={setAppleActiveTabIndex}
                />
              </TabPanel>

              <TabPanel header="🤖 Google Play Store">
                <GooglePlayStoreTab
                  googleConfig={googleConfig}
                  featureSettings={featureSettings}
                  setFeatureSettings={setFeatureSettings}
                  loading={loading}
                  setLoading={setLoading}
                  tenantId={tenantId}
                  tenantData={tenantData}
                  toast={toast}
                  fetchSettings={fetchSettings}
                  activeTabIndex={googleActiveTabIndex}
                  setActiveTabIndex={setGoogleActiveTabIndex}
                />
              </TabPanel>
            </TabView>
          </div>
        </div>
      )}

      <Toast ref={toast} className="mt-header-height" />
    </div>
  );
}

export default StoreConfiguration;
